import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { defineMessages, useIntl } from 'react-intl';
import './Carousel.scss';

const messages = defineMessages({
  listingRange: {
    id: 'listing_range',
    defaultMessage: 'Displaying {start}–{end} of {total}',
  },
  listingSingleItem: {
    id: 'listing_single_item',
    defaultMessage: 'Displaying {end} of {total}',
  },
});

interface CarouselRange {
  start: number;
  end: number;
  total: number;
}

export interface CarouselProps {
  children: React.ReactNode;
  /** When set, the carousel does not loop. Called from the next control on the last slide. */
  onEdgeNext?: () => void;
  /** When set, the carousel does not loop. Called from the previous control on the first slide. */
  onEdgePrev?: () => void;
  /** Slide index after the page of children changes. */
  startIndex?: number;
  /** Changes when a new page of children is shown, so the carousel jumps to startIndex. */
  pageKey?: string | number;
  /** Listing range rendered below the carousel pagination dots when needed. */
  range?: CarouselRange;
}

const LeftArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const RightArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 18l6-6-6-6" />
  </svg>
);

export const Carousel: React.FC<CarouselProps> = ({
  children,
  onEdgeNext,
  onEdgePrev,
  startIndex = 0,
  pageKey,
  range,
}) => {
  const intl = useIntl();
  const onEdgeNextRef = React.useRef(onEdgeNext);
  const onEdgePrevRef = React.useRef(onEdgePrev);
  onEdgeNextRef.current = onEdgeNext;
  onEdgePrevRef.current = onEdgePrev;

  const paged = Boolean(onEdgeNext || onEdgePrev);
  const slideCount = React.Children.count(children);
  const safeStart = slideCount > 0 ? Math.min(startIndex, slideCount - 1) : 0;

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: !paged,
    startIndex: safeStart,
  });
  const [selectedIndex, setSelectedIndex] = useState(safeStart);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit({ loop: !paged, startIndex: safeStart });
    emblaApi.scrollTo(safeStart, true);
  }, [emblaApi, paged, safeStart, slideCount, pageKey]);

  const scrollPrev = useCallback(() => {
    if (!emblaApi) return;
    if (emblaApi.canScrollPrev()) {
      emblaApi.scrollPrev();
      return;
    }
    onEdgePrevRef.current?.();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (!emblaApi) return;
    if (emblaApi.canScrollNext()) {
      emblaApi.scrollNext();
      return;
    }
    onEdgeNextRef.current?.();
  }, [emblaApi]);

  const scrollTo = useCallback(
    (index: number) => {
      if (emblaApi) emblaApi.scrollTo(index);
    },
    [emblaApi],
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  const displayButtons = slideCount > 1 || paged;
  const displayDots = scrollSnaps.length > 1;
  const displayRange = range && (range.start > 1 || range.end <= range.total);
  const prevDisabled = paged && selectedIndex === 0 && !onEdgePrev;
  const nextDisabled =
    paged &&
    scrollSnaps.length > 0 &&
    selectedIndex === scrollSnaps.length - 1 &&
    !onEdgeNext;

  const MAX_VISIBLE_DOTS = 6;
  const TRAILING_MARGIN = 1;
  const totalDots = scrollSnaps.length;
  const windowStart =
    totalDots <= MAX_VISIBLE_DOTS
      ? 0
      : Math.min(
          Math.max(selectedIndex - (MAX_VISIBLE_DOTS - 1 - TRAILING_MARGIN), 0),
          totalDots - MAX_VISIBLE_DOTS,
        );
  const visibleDotIndexes = Array.from(
    { length: Math.min(MAX_VISIBLE_DOTS, totalDots) },
    (_, offset) => windowStart + offset,
  );

  return (
    <div className="carousel-wrapper">
      <div className="carousel-container">
        {displayButtons && (
          <button
            className="nav-button prev"
            onClick={scrollPrev}
            disabled={prevDisabled}
            aria-label="Previous slide"
          >
            <LeftArrowIcon />
          </button>
        )}

        <div className="embla" ref={emblaRef}>
          <div className="embla__container">
            {React.Children.map(children, (child, index) => (
              <div className="embla__slide" key={index}>
                {child}
              </div>
            ))}
          </div>
        </div>

        {displayButtons && (
          <button
            className="nav-button next"
            onClick={scrollNext}
            disabled={nextDisabled}
            aria-label="Next slide"
          >
            <RightArrowIcon />
          </button>
        )}
      </div>

      {displayDots && (
        <div
          className="carousel-dots"
          role="tablist"
          aria-label="Carousel pagination"
        >
          {visibleDotIndexes.map((index) => (
            <button
              key={index}
              type="button"
              className={`carousel-dot${index === selectedIndex ? ' is-selected' : ''}`}
              onClick={() => scrollTo(index)}
              role="tab"
              aria-selected={index === selectedIndex}
              aria-label={`Go to slide ${index + 1} of ${totalDots}`}
            />
          ))}
        </div>
      )}

      {displayRange && (
        <p className="carousel-range">
          {range &&
            intl.formatMessage(
              range.start === range.end
                ? messages.listingSingleItem
                : messages.listingRange,
              range,
            )}
        </p>
      )}
    </div>
  );
};

export default Carousel;
